import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { submitLead } from '../../utils/leadApi';
import { DEALER_PROMISE } from '../../config/partners';
import { track } from '../../utils/track';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reusable, on-brand email capture card. Submits through submitLead (which
 * always succeeds for the user, queues locally if the backend is down).
 *
 * Props:
 *   title, blurb       : copy
 *   buttonLabel        : CTA text
 *   leadType           : backend lead_type
 *   collectName/Zip/Phone (bool): extra optional fields
 *   collectNote (bool) : optional free-text "what are you shopping for?" box.
 *                         Added 2026-08-09: every real lead to date arrived as
 *                         an email and sometimes a ZIP, no name, no phone, no
 *                         context, which isn't enough to help anyone or to be
 *                         worth anything. One optional box is the cheapest way
 *                         to learn the vehicle and the timeline. Lands in
 *                         details.note (JSONB: no backend change needed).
 *   notePlaceholder    : override the prompt in that box
 *   details            : object OR () => object of structured context
 *   analytics          : object OR () => object of extra lead_submit params
 *                         for GA4 (form name, coarse bands, opt-ins). Never
 *                         personal data: see src/utils/track.js.
 *   successTitle/Body  : confirmation copy
 *   compact            : tighter padding for embedding in pages
 */
export default function LeadCaptureCard({
  title,
  blurb,
  buttonLabel = 'Send it',
  leadType = 'newsletter',
  collectName = false,
  collectZip = false,
  collectPhone = false,
  collectNote = false,
  notePlaceholder = "What are you shopping for, and when? (optional)",
  details = null,
  analytics = null,
  successTitle = "You're in.",
  successBody = "We'll be in touch. No spam, no dealer nonsense.",
  downloadUrl = null,            // lead-magnet gate: deliver this file on success
  downloadLabel = 'Download',
  compact = false,
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [touched, setTouched] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());

  // lead_form_start: the first time anyone focuses or types in this form.
  const started = useRef(false);
  // `form` defaults to the page's first path segment ('home' on /), so a
  // report can split lead types by where the form sat without every page
  // naming its form. Pages with two forms pass their own.
  const resolveAnalytics = () => {
    let a = {};
    try { a = (typeof analytics === 'function' ? analytics() : analytics) || {}; } catch (_) { a = {}; }
    const section = (typeof window !== 'undefined' && window.location.pathname.split('/')[1]) || 'home';
    return { form: section, ...a };
  };
  const markStarted = () => {
    if (started.current) return;
    started.current = true;
    const a = resolveAnalytics();
    track('lead_form_start', { lead_type: leadType, form: a.form });
  };

  // Lead-magnet gate: the moment the lead is captured, start the download.
  useEffect(() => {
    if (status === 'done' && downloadUrl) {
      try {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.setAttribute('download', '');
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        /* the visible button below is the fallback */
      }
    }
  }, [status, downloadUrl]);

  const handleSubmit = async () => {
    setTouched(true);
    markStarted(); // Safari doesn't focus a clicked button
    if (status === 'submitting') return;
    if (!emailValid) {
      track('lead_form_error', { lead_type: leadType, form: resolveAnalytics().form, reason: email.trim() ? 'invalid_email' : 'missing_email' });
      return;
    }
    setStatus('submitting');
    const resolvedDetails = typeof details === 'function' ? details() : details;
    await submitLead({
      analytics: resolveAnalytics(),
      email: email.trim(),
      name: collectName ? name.trim() : undefined,
      zip: collectZip ? zip.trim() : undefined,
      phone: collectPhone ? phone.trim() : undefined,
      lead_type: leadType,
      details: {
        ...(resolvedDetails || {}),
        // Trimmed and capped; this is free text from a public form, and the
        // backend stores details as JSONB with a 20k payload ceiling.
        note: collectNote && note.trim() ? note.trim().slice(0, 1000) : undefined,
        _hp: company || undefined,
      },
    });
    setStatus('done');
  };

  if (status === 'done') {
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '2px solid #111111',
          borderRadius: 0,
          boxShadow: '4px 4px 0 #111',
          p: compact ? 2.5 : 3.5,
          textAlign: 'center',
        }}
      >
        <CheckCircleIcon sx={{ color: '#1748E8', fontSize: '2.2rem', mb: 1 }} />
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#111111', mb: 0.5 }}>
          {successTitle}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#111111' }}>{successBody}</Typography>
        {downloadUrl && (
          <Button
            component="a"
            href={downloadUrl}
            download
            data-track-context="lead_success"
            onClick={() => { if (typeof window !== 'undefined' && window.gtag) window.gtag('event', 'kit_download', { lead_type: leadType }); }}
            sx={{
              mt: 1.75,
              backgroundColor: '#FFE600',
              color: '#111111',
              border: '2px solid #111111',
              boxShadow: '4px 4px 0 #111',
              borderRadius: 0,
              padding: '9px 24px',
              fontWeight: 700,
              textTransform: 'none',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              '&:hover': { backgroundColor: '#FFE600', transform: 'translate(-2px, -2px)', boxShadow: '6px 6px 0 #111' },
            }}
          >
            {downloadLabel}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '2px solid #111111',
        borderRadius: 0,
        p: compact ? { xs: 2.5, md: 3 } : { xs: 3, md: 4 },
        boxShadow: '4px 4px 0 #111',
      }}
    >
      {title && (
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.4rem' }, color: '#111111', mb: 0.75 }}>
          {title}
        </Typography>
      )}
      {blurb && (
        <Typography sx={{ fontSize: '14px', color: '#333333', mb: 2, lineHeight: 1.6 }}>{blurb}</Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }} onFocus={markStarted} onInput={markStarted}>
        {collectName && (
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            size="small"
            fullWidth
            sx={fieldSx}
          />
        )}
        <TextField
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="you@email.com"
          size="small"
          fullWidth
          type="email"
          error={touched && !emailValid}
          helperText={touched && !emailValid ? 'Enter a valid email' : undefined}
          sx={fieldSx}
        />
        {(collectZip || collectPhone) && (
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {collectZip && (
            <TextField
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
              placeholder="ZIP"
              size="small"
              sx={{ ...fieldSx, maxWidth: 120 }}
            />
          )}
          {collectPhone && (
            <TextField
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone (optional)"
              size="small"
              sx={{ ...fieldSx, flex: 1, minWidth: 160 }}
            />
          )}
        </Box>
        )}

        {collectNote && (
          <TextField
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            placeholder={notePlaceholder}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            sx={fieldSx}
          />
        )}

        {/* Honeypot: hidden from humans */}
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
        />

        <Button
          onClick={handleSubmit}
          disabled={status === 'submitting'}
          sx={{
            backgroundColor: '#111111',
            color: '#fff',
            borderRadius: 0,
            padding: '10px 24px',
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '0.95rem',
            mt: 0.5,
            '&:hover': { backgroundColor: '#333333' },
            '&:disabled': { backgroundColor: '#777777', color: '#fff' },
          }}
        >
          {status === 'submitting' ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : buttonLabel}
        </Button>
        <Typography sx={{ fontSize: '12.5px', color: '#333333', lineHeight: 1.5, mt: 0.5 }}>{DEALER_PROMISE.short}</Typography>
      </Box>
    </Box>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 0, backgroundColor: '#F4F4F4' },
  '& .MuiFormHelperText-root': { mx: 0.5 },
};
